import { z } from 'zod';
import type { TenantId } from '@/modules/tenant/domain/entities/tenant-ids';
import {
  AnswerOptionSchema,
  type Answers,
} from '../../domain/entities/answer.entity';

/**
 * O simulado como ele está no navegador: o que a pessoa marcou e, depois do
 * cadastro, quando ele foi enviado.
 */
export interface SimuladoDraft {
  answers: Answers;
  /** ISO 8601. `null` enquanto o ensaio não foi enviado. */
  submittedAt: string | null;
}

const EMPTY: SimuladoDraft = { answers: {}, submittedAt: null };

/**
 * As chaves do `answers` viram texto no JSON e voltam como texto. O
 * `z.coerce.number()` desfaz isso — sem ele, `answers[42]` nunca acharia a
 * resposta guardada em `"42"`.
 */
const SimuladoDraftSchema = z.object({
  answers: z.record(z.coerce.number(), AnswerOptionSchema),
  submittedAt: z.string().nullable(),
});

/**
 * O simulado no `localStorage` — mesmo papel do `GabaritoDraftStore` do
 * `enamed-feedback-lp`: **responder não exige conta, enviar exige**, e entre a
 * última questão e o fim do cadastro há um formulário inteiro e o risco banal
 * de um reload. O rascunho garante que nada disso apaga o que foi marcado.
 *
 * **Continua guardado depois do envio**, ao contrário de lá: o diagnóstico é
 * calculado aqui, a partir das respostas. Some só quando a pessoa refaz.
 *
 * A chave leva a prova porque, em dev, todas rodam no mesmo `localhost:3005` e
 * dividiriam o mesmo rascunho (em produção cada prova tem o seu domínio). E
 * leva a edição porque as respostas são guardadas pelo número da questão: a
 * "Questão 3" de 2027 é outra questão, e o rascunho de 2026 não pode
 * respondê-la.
 *
 * Toda leitura e escrita é defensiva: aba anônima, cota estourada ou site com
 * dados bloqueados fazem o `localStorage` lançar, e perder o rascunho é ruim,
 * mas derrubar a tela é pior.
 */
export class SimuladoDraftStore {
  private readonly key: string;

  constructor(tenant: TenantId, edition: number) {
    this.key = `exams-capture-lp:${tenant}:${edition}:simulado`;
  }

  read(): SimuladoDraft {
    try {
      const raw = localStorage.getItem(this.key);
      if (!raw) return EMPTY;
      return SimuladoDraftSchema.parse(JSON.parse(raw));
    } catch {
      // Formato de uma versão anterior, ou storage indisponível: começar do
      // zero é melhor do que travar em algo ilegível.
      return EMPTY;
    }
  }

  save(draft: SimuladoDraft): void {
    try {
      localStorage.setItem(this.key, JSON.stringify(draft));
    } catch {
      // Sem espaço ou sem permissão. O simulado segue em memória.
    }
  }

  clear(): void {
    try {
      localStorage.removeItem(this.key);
    } catch {
      // Idem.
    }
  }
}
